import { cloneDeep, compact, isEmpty, isNil, sortBy } from 'lodash';
import {
  MutableRefObject,
  ReactElement,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useScratchpadServiceContext } from '../../../../../components/machine/scratchpad/scratchpad-service-context-provider';
import { logger } from '../../../../../util/logger';
import {
  SaveFlowOptions,
  useFlowServices,
} from '../../hook/flow-service.hooks';
import { usePersistedSelectedFlowItemIds } from '../../hook/flow-ui.hooks';
import {
  ApplicationDefaultParameterSet,
  ApplicationDefaultParameterSetBaseFormProps,
  Flow,
  FlowItem,
  FlowItemArrayType,
  FlowItemText,
  FlowPanel,
  FlowSaveResult,
  FlowSection,
  FlowSelectedItemInfo,
  FlowState,
} from '../../model/flow.types';
import {
  createSectionsFromFlowItems,
  instanceOfHelper,
} from '../../util/flow-item.util';
import { FlowActionBarEditActionsProps } from '../action/flow-action-bar-edit-actions.types';
import {
  FlowItemEditProps,
  FlowItemMultiEditProps,
} from '../flow-item/edit/flow-item-edit.types';
import { FlowItemProps } from '../flow-item/view/flow-item.types';
import { TeachDetailProps } from '../teach/teach.types';
import { ItemAction } from './flow-item-action-popover';
import { FlowSectionsProps } from './flow-sections';

export enum FlowUpdateMethod {
  LOCAL_ONLY = 'local_only',
  SERVER = 'server',
  SERVER_WITH_OPTIMISTIC_UPDATE = 'server_with_optimistic_update',
}

export interface FlowSectionsRef {
  toggleCollapseAll: (collapsed: boolean) => void;
}

type FlowRefComponentRegistryEntry<T> = (props: T) => ReactElement | null;
type FlowRefComponentRegistryEntryWithRef<T, P> = ReturnType<
  typeof forwardRef<T, P>
>;

interface FlowRefComponentRegistry {
  ApplicationSettingsForm: FlowRefComponentRegistryEntry<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ApplicationDefaultParameterSetBaseFormProps<any>
  >;
  FlowActionBarEditActions: FlowRefComponentRegistryEntry<FlowActionBarEditActionsProps>;
  FlowItem: FlowRefComponentRegistryEntry<FlowItemProps>;
  FlowItemEdit: FlowRefComponentRegistryEntry<FlowItemEditProps>;
  FlowItemMultiEdit: FlowRefComponentRegistryEntry<FlowItemMultiEditProps>;
  TeachDetail: FlowRefComponentRegistryEntry<TeachDetailProps>;
  FlowSections: FlowRefComponentRegistryEntryWithRef<
    FlowSectionsRef,
    FlowSectionsProps
  >;
  quickActions?: Record<string, FlowQuickAction>;
}

export type SaveFlowFn = (
  items: FlowItemArrayType[],
  options: SaveFlowOptions & {
    updateMethod: FlowUpdateMethod;
    applicationDefaultParameterSet?: ApplicationDefaultParameterSet;
  }
) => Promise<FlowSaveResult | undefined>;

export interface FlowRef {
  flowItems: FlowItemArrayType[];
  flowItemTextArr: (FlowItemText | undefined)[];
  flowPanel?: FlowPanel;
  flowState: FlowState;
  flowSectionsRef: MutableRefObject<FlowSectionsRef | null>;
  sections: FlowSection[];
  selectedItemDetails: FlowSelectedItemDetails;
  addItemsToFlow: (items: FlowItem[]) => Promise<void>;
  onUpdateItems: (
    items: FlowItem[],
    applicationDefaultParameterSet?: ApplicationDefaultParameterSet
  ) => Promise<void>;
  setFlowItems: (items: FlowItemArrayType[]) => void;
  setFlowState: (flowState: FlowState) => void;
  setFlowPanel: (flowPanel: FlowPanel | undefined) => void;
  setSelectedItemIds: (
    selectedItemIds: string[],
    nextFlowPanel?: FlowPanel
  ) => void;
  saveFlow: SaveFlowFn;
  toggleCollapseAll: (collapsed: boolean) => void;
  updateFlowConfig: <FlowConfig>(
    config: Partial<FlowConfig>
  ) => Promise<FlowSaveResult | undefined>;
  componentRegistry: FlowRefComponentRegistry;
}

export interface FlowSelectedItemDetails {
  selectedFlowItemInfo: FlowSelectedItemInfo;
  selectedFlowItems: FlowItem[];
  selectedFlowItemsPersisted: FlowItem[];
  selectedFlowItemsSorted: FlowItem[];
  selectedItemActualIndex: number;
  selectedItemIds: string[];
  selectedPersistedItemIds: string[];
}

const FlowRefContext = createContext<FlowRef | undefined>(undefined);

const useFlowRefContext = () => {
  return useContext(FlowRefContext) as FlowRef;
};

export interface FlowQuickAction {
  ui: Omit<ItemAction, 'action'> & {
    action: string;
  };
  handler: (item: FlowItem, onAction?: () => void) => void;
  condition?: (item: FlowItem) => boolean;
}

interface FlowUIRefProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  flow: Flow<any>;
  onFlowChange: (
    updatedFlow: Flow,
    options?: SaveFlowOptions
  ) => Promise<FlowSaveResult>;
  componentRegistry: FlowRefComponentRegistry;
}

const useCreateFlowRef = (props: FlowUIRefProps): FlowRef => {
  const { flow, onFlowChange, componentRegistry } = props;

  const [flowItems, setFlowItems] = useState<FlowItemArrayType[]>([]);
  const [flowState, setFlowState] = useState<FlowState>(FlowState.DEFAULT);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const selectedPersistedItemIds =
    usePersistedSelectedFlowItemIds(selectedItemIds);

  const { flowService, flowItemService } = useFlowServices();
  const { isSaving: isScratchpadSaving } = useScratchpadServiceContext();

  const [flowPanel, setFlowPanel] = useState<FlowPanel | undefined>(undefined);

  const flowSectionsRef = useRef<FlowSectionsRef | null>(null);

  const selectedItemDetails = useMemo<FlowSelectedItemDetails>(() => {
    const filteredItems = flowItems.filter(
      (item) => !instanceOfHelper(item)
    ) as FlowItem[];

    const localFlowItems = cloneDeep(filteredItems);

    const selectedFlowItemInfo = flowItemService.getFlowSelectedItemInfo(
      flow,
      selectedItemIds
    );

    const selectedFlowItems = compact(
      selectedItemIds.map((id) => localFlowItems.find((i) => i.id === id))
    );

    const selectedFlowItemsPersisted = compact(
      selectedPersistedItemIds.map((id) =>
        localFlowItems.find((i) => i.id === id)
      )
    );

    const selectedFlowItemsSorted = sortBy(selectedFlowItems);
    const selectedItemActualIndex = localFlowItems.findIndex(
      (i) => !isNil(i.id) && i.id === selectedFlowItems[0]?.id
    );

    return {
      selectedFlowItemInfo,
      selectedFlowItems,
      selectedFlowItemsPersisted,
      selectedFlowItemsSorted,
      selectedItemActualIndex,
      selectedItemIds,
      selectedPersistedItemIds,
    };
  }, [
    flow,
    flowItems,
    selectedPersistedItemIds,
    selectedItemIds,
    flowItemService,
  ]);

  const updateFlowConfig = useCallback(
    async (config: Partial<unknown>): Promise<FlowSaveResult | undefined> => {
      let items = flowItems.filter(
        (item) => !instanceOfHelper(item)
      ) as FlowItem[];

      if (items.length === 0) {
        items = flow.items;
      }

      const errors = await flowService.validate({ ...flow, items });

      // We want to make sure there are no errors before updating the flow config
      //   so that a (pending) flow save with errors isn't overwritten accidentally
      if (isEmpty(errors)) {
        return onFlowChange(
          {
            ...flow,
            config: {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ...(flow.config as any),
              ...config,
            },
          },
          {
            saveAsDelta: true,
          }
        );
      } else {
        logger.info('flow config update skipped due to flow validation errors');
      }
    },
    [flow, flowItems, flowService, onFlowChange]
  );

  const saveFlow: SaveFlowFn = useCallback(
    async (items, options): Promise<FlowSaveResult | undefined> => {
      const { updateMethod, applicationDefaultParameterSet, saveAsDelta } =
        options;

      const filteredItems = items.filter(
        (item) => !instanceOfHelper(item)
      ) as FlowItem[];

      let result: FlowSaveResult | undefined = undefined;
      if (updateMethod === FlowUpdateMethod.LOCAL_ONLY) {
        // this condition is used when entering teach mode as well as when adding
        // flow items

        // optimistically set new flow items by leveraging pre-save
        // and merging results back into the original items before setting
        // state
        const preSavedItems = flowItemService.preSave(
          flow.config,
          filteredItems
        );
        let indexOffset = 0;
        for (let i = 0; i < items.length; i++) {
          if (instanceOfHelper(items[i])) {
            indexOffset++;
          } else {
            items[i] = preSavedItems[i - indexOffset];
          }
        }
        setFlowItems(items);
      } else {
        const updatedFlow: Flow = { ...flow, items: filteredItems };

        if (applicationDefaultParameterSet) {
          updatedFlow.defaultParameterSet = applicationDefaultParameterSet;
        }

        if (
          updateMethod === FlowUpdateMethod.SERVER_WITH_OPTIMISTIC_UPDATE &&
          // We don't want to prematurely update the flow if we know there
          // is an api call in-flight
          !isScratchpadSaving
        ) {
          setFlowItems(filteredItems);
        }

        result = await onFlowChange(updatedFlow, {
          saveAsDelta: saveAsDelta ?? false,
        });

        if (result.scratchpad) {
          const savedItems = (result.scratchpad.appData?.items ??
            []) as FlowItem[];
          setFlowItems(savedItems);
        } else if (result.errors) {
          setFlowItems(filteredItems);
        }
      }
      return result;
    },
    [flowItemService, flow, onFlowChange, isScratchpadSaving]
  );

  const onUpdateItems = useCallback(
    async (
      items: FlowItem[],
      applicationDefaultParameterSet?: ApplicationDefaultParameterSet
    ) => {
      const clonedItems = cloneDeep(flowItems as FlowItem[]);
      for (const idx in items) {
        const id = items[idx].id;
        const existingIdx = clonedItems.findIndex((fi) => fi.id === id);
        if (existingIdx > -1) {
          clonedItems[existingIdx] = items[idx];
        }
      }
      await saveFlow(clonedItems, {
        updateMethod: FlowUpdateMethod.SERVER,
        applicationDefaultParameterSet: applicationDefaultParameterSet,
        saveAsDelta: true,
      });
    },
    [flowItems, saveFlow]
  );

  const addItemsToFlow = useCallback(
    async (items: FlowItem[]): Promise<void> => {
      const newItems = flowItems.concat(items);
      await saveFlow(newItems, {
        updateMethod: FlowUpdateMethod.SERVER_WITH_OPTIMISTIC_UPDATE,
        saveAsDelta: true,
      });
    },
    [flowItems, saveFlow]
  );

  const flowItemTextArr = useMemo(() => {
    return flowItemService.buildFlowItemTextArr(flowItems);
  }, [flowItemService, flowItems]);

  const sections = useMemo<FlowSection[]>(() => {
    return createSectionsFromFlowItems(flowItems);
  }, [flowItems]);

  const setSelectedItemIdsWrapper = useCallback(
    (selectedItemIds: string[], nextFlowPanel?: FlowPanel) => {
      if (selectedItemIds.length > 0) {
        setFlowPanel(nextFlowPanel ?? FlowPanel.MULTI_EDIT);
      } else {
        setFlowPanel(undefined);
      }

      setSelectedItemIds(selectedItemIds);
    },
    []
  );

  const flowRef = useMemo(() => {
    return {
      componentRegistry,
      flowItemTextArr,
      flowItems,
      flowPanel,
      flowSectionsRef,
      flowState,
      sections,
      selectedItemDetails,
      addItemsToFlow,
      onUpdateItems,
      setSelectedItemIds: setSelectedItemIdsWrapper,
      saveFlow,
      setFlowItems,
      setFlowPanel,
      setFlowState,
      toggleCollapseAll:
        flowSectionsRef.current?.toggleCollapseAll ?? (() => undefined),
      updateFlowConfig,
    };
  }, [
    componentRegistry,
    flowItemTextArr,
    flowItems,
    flowPanel,
    flowState,
    sections,
    selectedItemDetails,
    addItemsToFlow,
    onUpdateItems,
    setSelectedItemIdsWrapper,
    saveFlow,
    updateFlowConfig,
  ]);

  return flowRef;
};

export { FlowRefContext, useCreateFlowRef, useFlowRefContext };
